import React, { useEffect, useState } from 'react';
import { Activity, Heart, Utensils, Brain, TrendingUp } from 'lucide-react';
import { PILLAR_LABELS } from '../lib/pillars';
import api from '../services/api';
import ScoreBreakdownPopover from './ScoreBreakdownPopover';

/**
 * WellnessIQCard — the dashboard hero. One composite Wellness IQ + four
 * sub-IQ rings (Movement, Recovery, Mental, Nutrition).
 *
 * Nikki is the connective tissue across all pillars — she is not a pillar
 * herself, so she doesn't have her own ring.
 *
 * Sub-IQs that don't have enough data yet render as "—" with a soft prompt.
 * No invented numbers, ever.
 */

const PILLAR_META = {
  movement:  { label: PILLAR_LABELS.movement,  icon: Activity, color: '#CCFF00' },
  recovery:  { label: PILLAR_LABELS.recovery,  icon: Heart,    color: '#22d3ee' },
  mental:    { label: PILLAR_LABELS.mental, short: PILLAR_LABELS.mental, icon: Brain, color: '#a855f7' },
  nutrition: { label: PILLAR_LABELS.nutrition, icon: Utensils, color: '#fb923c' },
};

const Ring = ({ value, color, size = 64, label, tkey, Icon }) => {
  const r = size / 2 - 5;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value ?? 0));
  const offset = c * (1 - pct / 100);
  const display = value == null ? '—' : Math.round(value);
  return (
    <div className="flex flex-col items-center gap-1.5" data-testid={`wellness-ring-${tkey || label.toLowerCase()}`}>
      <div className="relative" style={{ width: size, height: size }}>
        <svg viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
          <circle cx={size/2} cy={size/2} r={r} stroke="#27272a" strokeWidth="4" fill="none" />
          {value != null && (
            <circle
              cx={size/2} cy={size/2} r={r}
              stroke={color} strokeWidth="4" fill="none"
              strokeLinecap="round"
              strokeDasharray={c}
              strokeDashoffset={offset}
              style={{ transition: 'stroke-dashoffset 800ms cubic-bezier(.2,.8,.2,1)' }}
            />
          )}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <Icon className="w-4 h-4" style={{ color: value == null ? '#52525b' : color }} />
        </div>
      </div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="font-secondary font-bold text-base" style={{ color: value == null ? '#52525b' : color }}>{display}</div>
    </div>
  );
};

const WellnessIQCard = ({ onPillarClick }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.getWellnessIQ();
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    // Listen for cross-component refresh signals fired by DailyCheckinCard etc.
    const onRefresh = () => { load(); };
    window.addEventListener('wellness:refresh', onRefresh);
    return () => {
      cancelled = true;
      window.removeEventListener('wellness:refresh', onRefresh);
    };
  }, []);

  const overall = data?.wellness_iq;
  const sub = data?.sub_scores || {};
  const overallColor = overall == null ? '#52525b' : overall >= 80 ? '#CCFF00' : overall >= 60 ? '#facc15' : '#fb923c';

  return (
    <div className="hpme-card p-5 md:p-6" data-testid="wellness-iq-card">
      <div className="hpme-card-corner" />
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-primary" />
          <span className="text-[10px] uppercase tracking-[0.2em] text-primary font-secondary font-bold">YOUR WELLNESS IQ</span>
        </div>
        {/* Composite formula transparency — 30% Movement + 25% Recovery + 25% Mental + 20% Nutrition */}
        <ScoreBreakdownPopover
          breakdown={data?.score_breakdown?.composite}
          label="Wellness IQ formula"
          finalScore={overall}
          color="#CCFF00"
          testId="wellness-iq-breakdown"
        />
      </div>

      <div className="flex flex-col md:flex-row items-center gap-6">
        {/* Big overall ring */}
        <div className="relative" style={{ width: 140, height: 140 }} data-testid="wellness-iq-overall">
          <svg viewBox="0 0 140 140" className="-rotate-90">
            <circle cx="70" cy="70" r="62" stroke="#27272a" strokeWidth="8" fill="none" />
            {overall != null && (
              <circle
                cx="70" cy="70" r="62"
                stroke={overallColor} strokeWidth="8" fill="none"
                strokeLinecap="round"
                strokeDasharray={2 * Math.PI * 62}
                strokeDashoffset={2 * Math.PI * 62 * (1 - Math.max(0, Math.min(100, overall)) / 100)}
                style={{ transition: 'stroke-dashoffset 900ms cubic-bezier(.2,.8,.2,1)' }}
              />
            )}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="font-secondary font-bold text-5xl" style={{ color: overallColor }}>{overall == null ? '—' : overall}</div>
            <div className="text-[9px] uppercase tracking-widest text-zinc-500 mt-1">WELLNESS IQ</div>
          </div>
        </div>

        {/* Sub-rings — 4 pillars, each with a (i) breakdown popover */}
        <div className="flex-1 grid grid-cols-4 gap-2 md:gap-3 w-full">
          {Object.entries(PILLAR_META).map(([key, meta]) => (
            <div key={key} className="relative flex flex-col items-center">
              <button
                onClick={() => onPillarClick?.(key)}
                className="hover:opacity-80 transition-opacity cursor-pointer"
                data-testid={`wellness-pillar-${key}`}
              >
                <Ring
                  value={sub[key]}
                  color={meta.color}
                  label={meta.short || meta.label}
                  tkey={key}
                  Icon={meta.icon}
                />
              </button>
              <div className="mt-1">
                <ScoreBreakdownPopover
                  breakdown={data?.score_breakdown?.[key]}
                  label={`${meta.label} formula`}
                  finalScore={sub[key]}
                  color={meta.color}
                  testId={`pillar-breakdown-${key}`}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {overall == null && !loading && (
        <p className="text-xs text-zinc-500 mt-4 text-center" data-testid="wellness-iq-empty">
          Log a check-in, take the Mobility baseline, or chat with Nikki — your Wellness IQ unfolds as you use the app.
        </p>
      )}

      {/* TRUE PER-DAY model: on a no-check-in day we show the standing baseline
          (Movement + Nutrition) and gently invite today's check-in to light up
          Recovery + Mental — never a fake number, never guilt. */}
      {data && data.checked_in_today === false && overall != null && (
        <div
          className="mt-4 flex items-start gap-2.5 rounded-lg border border-primary/20 bg-primary/5 px-3.5 py-2.5"
          data-testid="wellness-checkin-nudge"
        >
          <TrendingUp className="w-4 h-4 text-primary mt-0.5 shrink-0" />
          <p className="text-xs text-zinc-400 leading-relaxed">
            Showing your <span className="text-zinc-200">standing baseline</span> for today.
            {' '}<span className="text-primary">Check in below</span> to complete today's Recovery &amp; Mental Readiness read.
          </p>
        </div>
      )}
    </div>
  );
};

export default WellnessIQCard;
